import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { AbstractControl, FormArray, ReactiveFormsModule } from '@angular/forms';
import { ScaleSliderComponent } from '@shared/components/scale-slider/scale-slider.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { TreatmentGroup } from '../../cia-form-groups';
import { symptomValueUsed } from '../../cia-validators';
import { CiaStepBase } from '../../cia-step-base';

// Step 4, Treatment planning: what limits the client, the P7 planning rows (the symptom clusters, one line each until one is opened) and the
// clinical impressions and treatment plan.
@Component({
  selector: 'cia-step-treatment',
  imports: [ReactiveFormsModule, ScaleSliderComponent, ...SharedMaterialUIModule],
  templateUrl: './cia-step-treatment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CiaStepTreatmentComponent extends CiaStepBase {
  // The step's form group, held by the form so its validity counts there.
  readonly group = input.required<TreatmentGroup>();

  // The clusters opened by hand; one that is not complete is always open so the error is seen.
  private readonly openedClusters = signal<ReadonlySet<number>>(new Set());

  // The two row sets of this step, as untyped arrays for the template.
  protected limitationRows(): FormArray {
    return this.group().controls.limitations as FormArray;
  }

  protected clusterRows(): FormArray {
    return this.group().controls.symptomClusters as FormArray;
  }

  // A symptom cluster has something filled in (it then shows "Filled in").
  protected clusterUsed(row: AbstractControl): boolean {
    return symptomValueUsed(row.value);
  }

  // A cluster is shown open when it was opened, or when it is incomplete so the error is seen.
  protected clusterOpen(index: number, row: AbstractControl): boolean {
    return this.openedClusters().has(index) || (row.hasError('ratingsRequired') && !this.readOnly());
  }

  // Remembers that a cluster was opened or closed by hand.
  protected setClusterOpen(index: number, open: boolean): void {
    this.openedClusters.update((current) => {
      const next = new Set(current);
      if (open) {
        next.add(index);
      } else {
        next.delete(index);
      }

      return next;
    });
  }
}
